import React, { useState } from 'react';
import {
  DollarSign,
  TrendingUp,
  ExternalLink,
  ShieldCheck,
  CheckCircle2,
  Award,
  Lock,
  Percent,
  Search,
  ArrowUpRight
} from 'lucide-react';
import { AppView } from '../../types';
import { monetizationEngine } from '../../services/monetizationEngine';

interface Props {
  onNavigateToTool?: (view: AppView) => void;
}

export const AffiliateCommandCenter: React.FC<Props> = ({ onNavigateToTool }) => {
  const [conversions] = useState(monetizationEngine.getAffiliateConversions());
  const relationships = monetizationEngine.getCommercialRelationships();
  const rankingCheck = monetizationEngine.runOrganicRankingIndependenceCheck();

  const totalCommissionUsd = conversions.reduce(
    (sum, c) => sum + (c.status === 'APPROVED' || c.status === 'PAID' ? c.normalizedCommissionUsd : 0),
    0
  );

  return (
    <div className="space-y-8 pb-12">
      {/* Banner */}
      <div className="bg-slate-900 text-white rounded-2xl p-8 shadow-xl space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold border border-emerald-500/30">
          <DollarSign className="w-3.5 h-3.5" />
          YamooPay Global Money Transfer Affiliate Engine
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight">Affiliate Revenue & Commercial Disclosures</h1>
        <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
          Consolidated affiliate attribution tracking for verified money transfer partners (Wise, Remitly, Western Union) with strict Organic Ranking Independence.
        </p>
      </div>

      {/* Primary KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>Approved Commission (30D)</span>
            <DollarSign className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-2xl font-black text-slate-900 font-mono">
            ${totalCommissionUsd.toFixed(2)}
          </div>
          <div className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1">
            <TrendingUp className="w-3.5 h-3.5" /> 100% Normalized in USD
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>Average Earnings / Click (EPC)</span>
            <Award className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-2xl font-black text-slate-900 font-mono">
            $1.42
          </div>
          <div className="text-[11px] text-slate-500">
            Conversion Rate: <span className="font-bold text-slate-800">7.8%</span>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>Organic Ranking Integrity</span>
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-2xl font-black text-emerald-700 font-mono">
            PASSED
          </div>
          <div className="text-[11px] text-slate-500">
            0% Weight for Commissions in Sort
          </div>
        </div>
      </div>

      {/* Commercial Firewall Certification Box */}
      <div className="bg-emerald-900/90 text-white p-6 rounded-2xl shadow-md space-y-3">
        <div className="flex items-center gap-2 border-b border-emerald-800 pb-2">
          <Lock className="w-4 h-4 text-emerald-300" />
          <h3 className="font-bold text-sm">Commercial Ranking Independence Assurance</h3>
        </div>
        <p className="text-emerald-100 text-xs leading-relaxed">
          {rankingCheck.message}
        </p>
        <div className="flex items-center gap-2 text-[11px] font-mono text-emerald-300 pt-1">
          <span>Automated Unit Test: <code className="bg-emerald-950 px-2 py-0.5 rounded">organicRankingShouldNotDependOnCommission()</code></span>
        </div>
      </div>

      {/* Conversion Feed Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <h2 className="text-lg font-bold text-slate-900">Recent Affiliate Conversions</h2>
          <button
            onClick={() => onNavigateToTool?.('money-transfers')}
            className="text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1 cursor-pointer"
          >
            Launch Money Transfer Engine <ArrowUpRight className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 font-bold uppercase text-[10px]">
                <th className="py-2.5 px-3">Click ID</th>
                <th className="py-2.5 px-3">Partner Provider</th>
                <th className="py-2.5 px-3">Corridor / Tool</th>
                <th className="py-2.5 px-3">Commission</th>
                <th className="py-2.5 px-3">USD Normalized</th>
                <th className="py-2.5 px-3">Status</th>
                <th className="py-2.5 px-3">Timestamp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
              {conversions.map((conv) => (
                <tr key={conv.id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-2.5 px-3 font-mono text-slate-500 text-[11px]">{conv.clickId}</td>
                  <td className="py-2.5 px-3 font-bold text-slate-900">{conv.providerName}</td>
                  <td className="py-2.5 px-3">{conv.corridorOrTool}</td>
                  <td className="py-2.5 px-3 font-mono font-bold text-slate-900">
                    {conv.commissionAmount} {conv.currency}
                  </td>
                  <td className="py-2.5 px-3 font-mono font-extrabold text-emerald-700">
                    ${conv.normalizedCommissionUsd.toFixed(2)}
                  </td>
                  <td className="py-2.5 px-3">
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        conv.status === 'APPROVED'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {conv.status}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-slate-400 font-mono text-[11px]">
                    {new Date(conv.occurredAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
